import { useState, type KeyboardEvent } from "react";
import { ApiError } from "../../api/client";
import type { Authorization, FoundPrinter, PrinterEntry, PrintingState } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { Dialog } from "../../ui/Dialog";

interface Props {
  authorization: Authorization;
  state: PrintingState;
  onAdded: (printer: PrinterEntry) => void;
  onClose: () => void;
}

/** What was picked: a found printer (with the computer it is installed on) or one entered by hand. */
interface Pick {
  key: string;
  printer: FoundPrinter;
  viaDeviceId: string | null;
  manual: boolean;
}

const IP = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

/**
 * "Printer toevoegen", as in the prototype: pick a printer the Tafo app for Windows found (installed
 * on a computer, or in the network), give it a name, done. Or enter a network printer by IP address.
 */
export function AddPrinterDialog({ authorization, state, onAdded, onClose }: Props) {
  const { api, toast } = useTill();
  const text = t.printing.addDialog;
  const [pick, setPick] = useState<Pick | null>(null);
  const [name, setName] = useState("");
  const [ip, setIp] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const added = (printer: FoundPrinter) => state.printers.some((item) => item.connection === printer.connection);

  const save = async () => {
    if (!pick || busy) return;
    let printer = pick.printer;
    if (pick.manual) {
      const match = IP.exec(ip.trim());
      if (!match || match.slice(1).some((part) => Number(part) > 255)) {
        setError(text.badIp);
        return;
      }
      printer = { ...printer, connection: `Netwerk ${ip.trim()}` };
    }
    setBusy(true);
    setError("");
    try {
      const answer = await api.request<{ printer: PrinterEntry }>("POST", "/device/printers", {
        staffToken: authorization.token,
        body: {
          name: name.trim(),
          model: printer.model,
          connection: printer.connection,
          viaDeviceId: pick.viaDeviceId,
          paper: printer.paper ?? 80,
        },
      });
      toast(text.done(answer.printer.name));
      onAdded(answer.printer);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  };

  const onEnter = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      void save();
    }
  };

  const row = (printer: FoundPrinter, key: string, viaDeviceId: string | null) => {
    const isAdded = added(printer);
    return (
      <button
        key={key}
        type="button"
        className={`btn found${pick?.key === key ? " on" : ""}`}
        disabled={!printer.receipt || isAdded}
        onClick={() => {
          setPick({ key, printer, viaDeviceId, manual: false });
          setName("");
          setError("");
        }}
      >
        <span>
          <b>{printer.model}</b>
          <br />
          <span className="note">{printer.connection}</span>
        </span>
        <span className="note">
          {isAdded ? text.added : !printer.receipt ? (printer.reason ?? "") : text.pick(printer.paper ?? 80)}
        </span>
      </button>
    );
  };

  const locals = state.found.local;
  return (
    <Dialog title={text.title} size="wide" onClose={onClose}>
      <p className="note">{text.note}</p>
      {locals.length === 0 ? (
        <div className="grp">
          <h3>{text.installedOn(text.thisComputer)}</h3>
          <p className="note">{text.nothingYet}</p>
        </div>
      ) : (
        locals.map((group) => (
          <div className="grp" key={group.deviceId}>
            <h3>{text.installedOn(group.deviceName)}</h3>
            <div className="foundlist">
              {group.printers.map((printer) => row(printer, `${group.deviceId}:${printer.connection}`, group.deviceId))}
            </div>
            <p className="note">{text.usbNote(group.deviceName)}</p>
          </div>
        ))
      )}
      <div className="grp">
        <h3>{text.network}</h3>
        {state.found.network.length === 0 ? (
          <p className="note">{text.nothingYet}</p>
        ) : (
          <div className="foundlist">
            {state.found.network.map((printer) => row(printer, `net:${printer.connection}`, null))}
          </div>
        )}
      </div>
      {pick && (
        <div className="grp">
          <h3>{pick.manual ? text.nameAndIp : text.nameStep}</h3>
          <div className="namestep">
            <input
              aria-label={text.nameStep}
              value={name}
              maxLength={60}
              placeholder={
                pick.manual ? text.manualPlaceholder : text.namePlaceholder(pick.printer.model.includes("Star"))
              }
              data-autofocus
              autoFocus
              onKeyDown={onEnter}
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
            <button type="button" className="btn primary" disabled={busy} onClick={() => void save()}>
              {text.add}
            </button>
          </div>
          {pick.manual && (
            <input
              className="ipfield"
              aria-label={text.ipLabel}
              inputMode="decimal"
              value={ip}
              placeholder={text.ipPlaceholder}
              onKeyDown={onEnter}
              onChange={(event) => {
                setIp(event.target.value);
              }}
            />
          )}
        </div>
      )}
      <p className="pinmsg" role="alert">
        {error}
      </p>
      <button
        type="button"
        className="linkbtn note"
        onClick={() => {
          setPick({
            key: "manual",
            printer: {
              model: text.networkPrinter,
              connection: "",
              network: true,
              paper: 80,
              receipt: true,
              reason: null,
            },
            viaDeviceId: null,
            manual: true,
          });
          setError("");
        }}
      >
        {text.manual}
      </button>
    </Dialog>
  );
}
